/**
 * Experiment significance calculator (T-164-003 — batch 4 S9).
 *
 * Pure statistics for the admin UI: a two-proportion z-test against the
 * control arm per metric (completion-rate, booking-rate, …). A variant is
 * flagged the winner when its p-value < 0.05 (two-sided, pooled SE), so the
 * admin dashboard can call experiments instead of eyeballing deltas.
 *
 * No side effects — unit-testable without a DOM.
 */

export interface ProportionInput {
  success: number;
  total: number;
}

export interface VariantRow extends ProportionInput {
  variant: string;
}

export interface MetricComparison {
  metric: string;
  control: ProportionInput;
  variants: VariantRow[];
  /** Each variant's p-value vs the control; null when data is insufficient. */
  results: VariantComparisonResult[];
}

export interface VariantComparisonResult {
  variant: string;
  controlRate: number;
  rate: number;
  delta: number;
  pValue: number | null;
  /** True when p < 0.05 — the variant is a statistically significant winner. */
  significant: boolean;
  winner: string | null;
}

/** A paste-able sheet for one metric (the admin paste surface shape). */
export interface CompareSheet {
  metric: string;
  control: ProportionInput;
  variants: VariantRow[];
}

export interface MetricComparison {
  metric: string;
  control: ProportionInput;
  variants: VariantRow[];
  results: VariantComparisonResult[];
}

/** Mean/cdf helpers (Abramowitz & Stegun 7.1.26). */
export function normalCdf(z: number): number {
  if (Number.isNaN(z)) return NaN;
  const sign = z < 0 ? -1 : 1;
  const x = Math.abs(z) / Math.sqrt(2);
  // erf approximation
  const t = 1 / (1 + 0.3275911 * x);
  const poly =
    t *
    (0.254829592 +
      t * (-0.284496736 + t * (1.421413741 + t * (-1.453152027 + t * 1.061405429))));
  const erf = 1 - poly * Math.exp(-x * x);
  return 0.5 * (1 + sign * erf);
}

export function pValueFromZ(z: number): number {
  if (Number.isNaN(z)) return NaN;
  return 2 * (1 - normalCdf(Math.abs(z)));
}

export const SIGNIFICANCE_ALPHA = 0.05;

/**
 * Two-proportion z-test (pooled standard error). Returns null when the test
 * is not meaningful (missing/invalid totals, zero successes on both sides).
 */
export function twoProportionZTest(
  control: ProportionInput,
  variant: ProportionInput
): { z: number; pValue: number; delta: number } | null {
  const cTotal = Number(control.total);
  const vTotal = Number(variant.total);
  if (!Number.isFinite(cTotal) || !Number.isFinite(vTotal) || cTotal < 2 || vTotal < 2) {
    return null;
  }
  const cSuccess = Number(control.success);
  const vSuccess = Number(variant.success);
  if (
    !Number.isFinite(cSuccess) ||
    !Number.isFinite(vSuccess) ||
    cSuccess < 0 ||
    vSuccess < 0 ||
    cSuccess > cTotal ||
    vSuccess > vTotal
  ) {
    return null;
  }

  const cRate = cSuccess / cTotal;
  const vRate = vSuccess / vTotal;
  const pooled = (cSuccess + vSuccess) / (cTotal + vTotal);
  const se = Math.sqrt(pooled * (1 - pooled) * (1 / cTotal + 1 / vTotal));

  if (se === 0) {
    // identical rates (or degenerate pooled) — mathematically equal, p = 1
    return { z: 0, pValue: 1, delta: vRate - cRate };
  }

  const z = (vRate - cRate) / se;
  return { z, pValue: pValueFromZ(z), delta: vRate - cRate };
}

/** Compare every variant row against the control for one metric. */
export function compareVariantsToControl(
  control: ProportionInput,
  variants: VariantRow[]
): VariantComparisonResult[] {
  return variants.map((v) => {
    const base: VariantComparisonResult = {
      variant: v.variant,
      controlRate: control.total ? control.success / control.total : 0,
      rate: v.total ? v.success / v.total : 0,
      delta: 0,
      pValue: null,
      significant: false,
      winner: null,
    };
    const test = twoProportionZTest(control, { success: v.success, total: v.total });
    if (!test) return base;
    const significant = test.pValue < SIGNIFICANCE_ALPHA;
    return {
      ...base,
      rate: v.total ? v.success / v.total : 0,
      delta: test.delta,
      pValue: test.pValue,
      significant,
      winner: significant ? v.variant : null,
    };
  });
}

/** Compute the comparison for an entire metric sheet (admin paste surface). */
export function compareMetrics(sheet: CompareSheet): MetricComparison {
  return {
    metric: sheet.metric,
    control: sheet.control,
    variants: sheet.variants,
    results: compareVariantsToControl(sheet.control, sheet.variants),
  };
}

/** Render a rate (0–1) as a percentage. */
export function formatRate(rate: number): string {
  return `${(rate * 100).toFixed(2)}%`;
}

/** Render a p-value (null → "—" when the test was not meaningful). */
export function formatPValue(pValue: number | null): string {
  if (pValue === null || Number.isNaN(pValue)) return '—';
  return pValue < 0.0001 ? '< 0.0001' : pValue.toFixed(4);
}

/** Winner badge label for the admin table. */
export function winnerLabel(result: VariantComparisonResult): string {
  return result.significant ? `WINNER (${result.variant})` : '—';
}